"use client";

import { useEffect } from "react";

import {
  deadClickElementLabel,
  deadClickRole,
  deadClickTarget,
  reportDeadClick,
} from "@/lib/dead-click-beacon";

// How long to watch for an effect after a tap on an interactive element before
// declaring the click "dead". Generous enough that a normal synchronous UI
// response (open a sheet, scroll, navigate, toggle) lands inside it, short enough
// that a genuinely-dead tap is caught while the intent is fresh. Tunable; the dbt
// layer ranks by affected_users so the odd slow-async false-positive is diluted.
const DEAD_WINDOW_MS = 800;
const SCROLL_EPSILON_PX = 4;

/**
 * Installs a document-level, capture-phase click listener that detects DEAD CLICKS
 * on JS-handler elements (button / role=button / onclick / cursor:pointer) — taps
 * that produce NO navigation, DOM change, scroll, or input-focus within
 * {@link DEAD_WINDOW_MS} — and feeds the owned `dead_click` beacon (events_raw,
 * rule 34). This is the per-element attribution layer for the site-wide dead-click
 * rate Clarity/PostHog can only report in aggregate (rule 65's js_error pattern).
 * Throttle + dedupe live in dead-click-beacon.ts so a rage-tap can't flood the sink.
 */
export default function DeadClickReporter() {
  useEffect(() => {
    if (typeof window === "undefined" || typeof MutationObserver === "undefined") return undefined;

    const onClick = (event: MouseEvent) => {
      try {
        const target = deadClickTarget(event.target);
        if (!target) return; // not a handler-driven element — nothing to judge

        const startUrl = window.location.href;
        const startScrollY = window.scrollY;
        const startActive = document.activeElement;
        let mutated = false;

        const observer = new MutationObserver(() => {
          mutated = true;
          observer.disconnect();
        });
        observer.observe(document.documentElement, {
          childList: true,
          subtree: true,
          attributes: true,
        });

        window.setTimeout(() => {
          try {
            observer.disconnect();
            const navigated = window.location.href !== startUrl;
            const scrolled = Math.abs(window.scrollY - startScrollY) > SCROLL_EPSILON_PX;
            const focusMoved =
              document.activeElement !== startActive &&
              (document.activeElement?.tagName === "INPUT" ||
                document.activeElement?.tagName === "TEXTAREA" ||
                document.activeElement?.tagName === "SELECT");
            // Alive if ANYTHING responded; dead only when the tap changed nothing.
            if (mutated || navigated || scrolled || focusMoved) return;
            reportDeadClick({
              element: deadClickElementLabel(target),
              role: deadClickRole(target),
              route: window.location.pathname,
            });
          } catch {
            // never let the reporter throw into the event loop
          }
        }, DEAD_WINDOW_MS);
      } catch {
        // never let the reporter throw into a user's click
      }
    };

    // Capture phase so we observe BEFORE the app's own handlers run + can't be
    // stopped by a stopPropagation() in a child handler.
    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, []);

  return null;
}
